import { Building, type LucideIcon, ScanLine, Sparkles, Sun, Video } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { env } from '@/constants/env';
import { useFlagPayload } from '@/hooks/use-flag';
import { parseStringToBool } from '@/utils/parseStringToBool';

import { useSelectedTenant } from './tenant-switcher-dialog';

type ShellNavPart = 'sunlight-simulation' | 'image-rendering' | 'tools';

export type ShellNavItem = {
  id: string;
  title: string;
  url: '/estates' | '/flatfinders' | '/image-rendering' | '/image-to-video' | '/shadowmap';
  /** Paths that keep this item highlighted, defaults to the url itself */
  activePaths?: string[];
  icon: LucideIcon;
};

/**
 * Main menu items shared by the sidebar and the search, so both follow the same
 * per-tenant `hide-sidebar-parts` flag and the IMAGE_RENDERING env switch.
 */
export const useShellNav = () => {
  const { t } = useTranslation();
  const { selectedTenant } = useSelectedTenant();
  const flagPayload =
    useFlagPayload<Record<string, { hide: ShellNavPart[] }>>('hide-sidebar-parts');

  const isPartHidden = (part: ShellNavPart) => {
    if (!selectedTenant || !flagPayload) return false;
    return flagPayload[selectedTenant.id]?.hide.includes(part) ?? false;
  };

  const showTools = !isPartHidden('tools');
  const showAiRendering = showTools && !isPartHidden('image-rendering');

  const mainItems: ShellNavItem[] = [
    {
      id: 'estates',
      title: t('estates'),
      url: '/estates',
      activePaths: ['/estates', '/projects'],
      icon: Building,
    },
  ];

  const toolItems: ShellNavItem[] = showTools
    ? [
        {
          id: 'flatfinder',
          title: t('flatfinderFormFlatfinder'),
          url: '/flatfinders',
          icon: ScanLine,
        },
        ...(showAiRendering
          ? [
              {
                id: 'ai-rendering',
                title: t('shellNavAiRendering'),
                url: '/image-rendering',
                icon: Sparkles,
              } as const,
            ]
          : []),
        ...(showAiRendering && parseStringToBool(env.IMAGE_RENDERING)
          ? [
              {
                id: 'image-to-video',
                title: t('shellNavImageToVideo'),
                url: '/image-to-video',
                icon: Video,
              } as const,
            ]
          : []),
        ...(!isPartHidden('sunlight-simulation')
          ? [
              {
                id: 'sunlight',
                title: t('shellNavSunlight'),
                url: '/shadowmap',
                icon: Sun,
              } as const,
            ]
          : []),
      ]
    : [];

  return { mainItems, toolItems, showAiRendering };
};
